import React, { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { currentWeek, addWeeks, weekRangeLabel } from "@/lib/week";
import { useCallWeek, callWeekTekst } from "@/lib/useCallWeek";
import { FileText, Copy, Download, ChevronLeft, ChevronRight, Sparkles, Mail } from "lucide-react";
import { MailConcept } from "@/components/MailConcept";
import { CoachCard } from "@/components/CoachCard";
import { openMailConcept, mailHref, selectNode } from "@/lib/mailConcept";
import { LoadError, errMessage } from "@/components/LoadError";
import { toast } from "sonner";

// Minimal Dutch markdown renderer: #/##/### headings, - bullets (+ ingesprongen subregels), **bold**.
function renderMarkdown(md) {
  const lines = (md || "").split("\n");
  const blocks = [];
  let list = [];
  const inline = (t) => t.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="font-semibold text-zinc-900">{p.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{p}</React.Fragment>);
  const flush = (k) => {
    if (!list.length) return;
    blocks.push(
      <ul key={`ul-${k}`} className="list-disc pl-5 space-y-0.5 my-1.5">
        {list.map((it) => (
          <li key={it.key} className="text-sm text-zinc-700 break-words">
            {inline(it.text)}
            {it.subs.length > 0 && (
              <ul className="pl-4 mt-0.5 space-y-0.5">
                {it.subs.map((s, j) => <li key={j} className={s.bullet ? "list-[circle]" : "list-none"}>{inline(s.text)}</li>)}
              </ul>
            )}
          </li>
        ))}
      </ul>);
    list = [];
  };
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    const bullet = /^[-*]\s+/.test(line.trimStart());
    if (line.startsWith("### ")) { flush(idx); blocks.push(<h4 key={idx} className="font-heading font-bold text-zinc-900 text-sm mt-3 mb-0.5 break-words">{inline(line.slice(4))}</h4>); }
    else if (line.startsWith("## ")) { flush(idx); blocks.push(<h3 key={idx} className="font-heading font-bold text-zinc-900 text-base mt-4 mb-1 pb-1 border-b border-zinc-100">{inline(line.slice(3))}</h3>); }
    else if (line.startsWith("# ")) { flush(idx); blocks.push(<h2 key={idx} className="font-heading font-black text-zinc-900 text-lg mt-1 mb-1">{inline(line.slice(2))}</h2>); }
    else if (line.trim() === "---") { flush(idx); blocks.push(<hr key={idx} className="my-3 border-zinc-200" />); }
    else if (/^\s{2,}\S/.test(line) && list.length) { list[list.length - 1].subs.push({ bullet, text: line.trimStart().replace(/^[-*]\s+/, "") }); }
    else if (bullet) { list.push({ key: idx, text: line.trimStart().replace(/^[-*]\s+/, ""), subs: [] }); }
    else if (line.trim() === "") { flush(idx); }
    else if (line.startsWith("_") && line.endsWith("_")) { flush(idx); blocks.push(<p key={idx} className="text-sm italic text-zinc-400 my-1">{line.slice(1, -1)}</p>); }
    else { flush(idx); blocks.push(<p key={idx} className="text-sm text-zinc-700 my-1 break-words">{inline(line)}</p>); }
  });
  flush("end");
  return blocks;
}

export default function Weekrapport() {
  const [locaties, setLocaties] = useState([]);
  const [locatie, setLocatie] = useState("");
  const [week, setWeek] = useState(currentWeek());
  const weekGekozen = useRef(false);
  const kiesWeek = (f) => { weekGekozen.current = true; setWeek(f); };
  const callWeek = useCallWeek(week);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metaError, setMetaError] = useState("");
  const [reportError, setReportError] = useState("");

  // Wis een eerder rapport zodra locatie/week wijzigt — nooit het verkeerde weekrapport tonen/exporteren.
  useEffect(() => { setReport(null); setReportError(""); }, [week, locatie]);

  // E-mailconcept: gewist bij elke wijziging en opnieuw geladen (nooit de mail van de verkeerde week tonen).
  const { user } = useAuth();
  const isOversight = user?.role === "oversight";
  const [mail, setMail] = useState(null);
  const [mailError, setMailError] = useState("");
  const [metCoach, setMetCoach] = useState(false);
  const mailReq = useRef(0);
  const locRef = useRef(locatie);
  locRef.current = locatie;
  const mailRef = useRef(null);
  const loadMail = () => {
    if (!locatie) return;
    const id = ++mailReq.current;
    api.get(`/weekrapport/mail?locatie=${encodeURIComponent(locatie)}&week=${encodeURIComponent(week)}${metCoach ? "&coach=1" : ""}`)
      .then((r) => { if (id === mailReq.current) { setMail(r.data); setMailError(""); } })
      .catch((e) => { if (id === mailReq.current) setMailError(errMessage(e)); });
  };
  // S1: een late coach-callback gebruikt altijd de loadMail van de ACTUELE week/coach-keuze.
  const loadMailRef = useRef(loadMail);
  loadMailRef.current = loadMail;
  useEffect(() => { setMail(null); setMailError(""); loadMail(); }, [week, locatie, metCoach]); // eslint-disable-line react-hooks/exhaustive-deps
  const mailKlaarzetten = () => {
    if (!mail) return;
    openMailConcept(mail, { locatie, isOversight, onCopyFailed: () => selectNode(mailRef.current) });
  };

  const loadMeta = () => {
    api.get("/meta").then((r) => {
      setLocaties(r.data.locations || []);
      setMetaError("");
      if (r.data.locations?.length) setLocatie((prev) => prev || r.data.locations[0]);
      if (r.data.huidigeCallWeek && !weekGekozen.current) setWeek(r.data.huidigeCallWeek);   // v15: open op de call-week, niet op de ISO-week
    }).catch((e) => setMetaError(errMessage(e)));
  };
  useEffect(() => { loadMeta(); }, []);

  const generate = async () => {
    if (!locatie) { toast.error("Kies eerst een locatie."); return; }
    setLoading(true);
    setReportError("");
    try {
      const r = await api.get(`/weekrapport?locatie=${encodeURIComponent(locatie)}&week=${encodeURIComponent(week)}`);
      setReport(r.data);
    } catch (e) {
      setReport(null);
      setReportError(errMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!report) return;
    try {
      await navigator.clipboard.writeText(report.markdown);
      toast.success("Rapport gekopieerd naar klembord");
    } catch {
      toast.error("Kopiëren mislukt — selecteer en kopieer handmatig.");
    }
  };

  const download = () => {
    if (!report) return;
    const blob = new Blob([report.markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = report.filename || "weekrapport.md";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Rapport gedownload");
  };

  const selectCls = "px-3 py-2 rounded-md border border-zinc-300 bg-white text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-blue-600";

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6" data-testid="weekrapport-page">
      <div className="flex items-center gap-2 mb-1">
        <FileText className="h-6 w-6 text-zinc-700" />
        <h1 className="font-heading font-black text-2xl text-zinc-900">Weekrapport</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-3 md:mb-4">Stel per locatie en week een kant-en-klaar verslag samen om naar de locatie te sturen. Puur op basis van vastgelegde data.</p>

      <LoadError error={metaError} onRetry={loadMeta} testid="weekrapport-meta-error" className="mb-4" />

      <div className="bg-white rounded-[10px] border border-zinc-200 p-4 mb-3 md:mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Locatie</label>
            <select className={`${selectCls} max-w-[200px]`} data-testid="weekrapport-locatie" value={locatie} onChange={(e) => setLocatie(e.target.value)}>
              {locaties.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Call-week</label>
            <div className="flex items-center gap-1">
              <button data-testid="weekrapport-week-prev" onClick={() => kiesWeek((w) => addWeeks(w, -1))} className="h-9 w-9 rounded-md border border-zinc-300 flex items-center justify-center text-zinc-500 hover:border-zinc-400"><ChevronLeft className="h-4 w-4" /></button>
              <div className="px-3 py-2 rounded-md bg-zinc-50 border border-zinc-200 text-sm font-medium text-zinc-800 min-w-[160px] text-center" data-testid="weekrapport-week-label">{callWeek ? callWeekTekst(callWeek) : `${week} · ${weekRangeLabel(week)}`}</div>
              <button data-testid="weekrapport-week-next" onClick={() => kiesWeek((w) => addWeeks(w, 1))} className="h-9 w-9 rounded-md border border-zinc-300 flex items-center justify-center text-zinc-500 hover:border-zinc-400"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
          <button data-testid="weekrapport-generate" onClick={generate} disabled={loading || !locatie}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 transition-colors disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2">
            <Sparkles className="h-4 w-4" /> {loading ? "Bezig…" : "Genereer rapport"}
          </button>
          <button data-testid="mail-klaarzetten" onClick={mailKlaarzetten} disabled={!mail}
            data-mailto-href={mail ? mailHref(mail) : ""}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 transition-colors disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2">
            <Mail className="h-4 w-4" /> Mail klaarzetten
          </button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_380px] items-start">
      <aside className="lg:order-last lg:sticky lg:top-4">{locatie && <CoachCard key={locatie} locatie={locatie} onChanged={(l) => { if (l === locRef.current) loadMailRef.current(); }} />}</aside>
      <div className="min-w-0">
      <LoadError error={mailError} onRetry={loadMail} testid="mail-load-error" className="mb-4" />
      {mail && (
        <label className="mb-2 flex flex-wrap items-center gap-2 text-sm text-zinc-700 select-none" data-testid="mail-coach-optie">
          <input type="checkbox" data-testid="mail-coach-checkbox" className="h-4 w-4 rounded border-zinc-300 accent-zinc-900"
            checked={metCoach} disabled={!mail.coachBeschikbaar && !metCoach} onChange={(e) => setMetCoach(e.target.checked)} />
          Coach-blik "Voor de locatie" meenemen
          {!mail.coachBeschikbaar && <span data-testid="mail-coach-hint" className="text-xs text-zinc-400">Nog geen recente coach-blik voor deze locatie</span>}
        </label>
      )}
      {mail && <div className="mb-3 md:mb-4"><MailConcept ref={mailRef} mail={mail} locatie={locatie} isOversight={isOversight} /></div>}

      <LoadError error={reportError} onRetry={generate} testid="weekrapport-load-error" className="mb-4" />

      {report && (
        <div className="bg-white rounded-[10px] border border-zinc-200 overflow-hidden" data-testid="weekrapport-result">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-zinc-100 bg-zinc-50">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">Voorbeeld</span>
            <div className="flex flex-wrap items-center gap-2">
              <button data-testid="weekrapport-copy" onClick={copy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 text-sm font-medium text-zinc-700 hover:border-zinc-400">
                <Copy className="h-4 w-4" /> Kopiëren
              </button>
              <button data-testid="weekrapport-mail" onClick={mailKlaarzetten} disabled={!mail} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-60">
                <Mail className="h-4 w-4" /> Mail klaarzetten
              </button>
              <button data-testid="weekrapport-download" onClick={download} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
                <Download className="h-4 w-4" /> Downloaden
              </button>
            </div>
          </div>
          <div className="p-5">{renderMarkdown(report.markdown)}</div>
        </div>
      )}
      </div>
      </div>
    </div>
  );
}
